/* eslint-disable react/jsx-props-no-spreading */
import ClassNames from 'classnames/bind';
import { bool, element, func, node, object, objectOf, oneOf, oneOfType, string } from 'prop-types';
import React, { Component } from 'react';
import * as styles from './Tooltip.css';

const cx = ClassNames.bind(styles);

/* istanbul ignore next */
class Tooltip extends Component {
    static propTypes = {
        tooltipData: oneOfType([element, func, string]).isRequired,
        /**
         *  Content to display onClick/onMouseOver of tooltip text
         */
        children: oneOfType([node, object, string]).isRequired,
        automationId: string,
        customTooltipTheme: objectOf(string),
        disableHideOnContentClick: bool,
        disabled: bool,
        dynamicPosition: bool,
        enableOverlay: bool,
        /**
         * If true tooltip content will display on click. By default it will open on mouseOver
         * Pass a function to get a callback on click of tooltipText
         */
        onClick: oneOfType([bool, func]),
        /**
         * If true tooltip content will display on mouseOver
         * Pass a function to get a callback on mouseOver of tooltipText
         */
        onMouseOver: oneOfType([bool, func]),
        /**
         * Placement for tooltip content
         */
        placement: oneOf(['bottom', 'top']),
        theme: string,
        visibleOnLoad: bool,
    };

    static defaultProps = {
        automationId: null,
        customTooltipTheme: {},
        disableHideOnContentClick: true,
        disabled: false,
        dynamicPosition: true,
        enableOverlay: false,
        onClick: false,
        onMouseOver: false,
        placement: 'bottom',
        theme: '',
        visibleOnLoad: false,
    };

    containerClicked = false;

    constructor(props) {
        super(props);

        const { visibleOnLoad } = this.props;

        this.state = {
            contentStyle: {},
            isVisible: visibleOnLoad,
        };
    }

    componentDidMount() {
        this.setTooltipPosition();
    }

    getTooltipPosition = () => {
        const { placement: placementProp } = this.props;
        const { placement: placementState } = this.state;
        const contentStyle = {};
        let placement = placementState || placementProp;

        try {
            let totalHeightOfContent;
            const { width: bodyWidth } = document.body.getBoundingClientRect();

            const {
                bottom: contentBottom,
                height: contentHeight,
                top: contentTop,
                width: contentWidth,
            } = this.tooltipContentRef.getBoundingClientRect();

            const {
                bottom: tooltipTextBottom,
                left: tooltipTextLeft,
                right: tooltipTextRight,
                top: tooltipTextTop,
                width: tooltipTextWidth,
            } = this.tooltipTextRef.getBoundingClientRect();

            const windowHeight =
                window.innerHeight ||
                document.documentElement.clientHeight ||
                document.body.clientHeight;

            // If content width is greater than body width then body width will be considered.
            if (contentWidth > bodyWidth) {
                contentStyle.width = bodyWidth;
                contentStyle.right = tooltipTextRight - bodyWidth;
                contentStyle.margin = 0;
            } else if (contentWidth < tooltipTextWidth) {
                contentStyle.left = 0;
            } else {
                // For center aligning tooltip content
                const tooltipHalfWidth =
                    Math.round(contentWidth / 2) - Math.round(tooltipTextWidth / 2);
                const tooltipRightSpace = bodyWidth - tooltipTextRight;
                const suffLeftSpace = tooltipHalfWidth < tooltipTextLeft;
                const suffRightSpace = tooltipHalfWidth < tooltipRightSpace;

                if (suffLeftSpace && suffRightSpace) {
                    contentStyle.left = -tooltipHalfWidth;
                } else if (!suffLeftSpace) {
                    contentStyle.left = -tooltipTextLeft;
                } else if (!suffRightSpace) {
                    delete contentStyle.left;
                    contentStyle.right = -tooltipRightSpace;
                }
            }

            if (contentTop < tooltipTextTop) {
                totalHeightOfContent = contentHeight + (contentBottom - tooltipTextTop);
                placement = tooltipTextTop - totalHeightOfContent > 0 ? 'top' : 'bottom';
            } else {
                totalHeightOfContent = contentHeight + (contentTop - tooltipTextBottom);
                placement =
                    tooltipTextBottom + totalHeightOfContent < windowHeight ? 'bottom' : 'top';
            }

            return { contentStyle, placement };
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error in Tooltip component', error);
        }

        return { contentStyle, placement };
    };

    getTooltipText = () => {
        const {
            children,
            customTooltipTheme: { tooltipTextClass },
            enableOverlay,
            onClick,
            disabled,
        } = this.props;

        const { isVisible } = this.state;

        const textAdditionalProps = {};
        const displayContent = () => this.toggleDisplayContent(true);
        const removeContent = () => this.toggleDisplayContent(false);

        if (onClick) {
            textAdditionalProps.onClick = () => this.toggleDisplayContent(!isVisible);

            if (!enableOverlay) {
                isVisible ? this.addOutsideClickListener() : this.removeOutsideClickListener();
            }
        } else {
            textAdditionalProps.onMouseOver = displayContent;
            textAdditionalProps.onMouseLeave = removeContent;
        }
        return (
            <button
                className={cx('tooltipText', tooltipTextClass)}
                ref={(tooltipTextRef) => {
                    this.tooltipTextRef = tooltipTextRef;
                }}
                type="button"
                {...textAdditionalProps}
                disabled={disabled}
            >
                {children}
            </button>
        );
    };

    setActionOnContainer = () => {
        this.containerClicked = true;
    };

    setTooltipPosition = () => {
        const { dynamicPosition } = this.props;
        const { isVisible } = this.state;

        if (isVisible && dynamicPosition) {
            const { contentStyle, placement } = this.getTooltipPosition();

            this.setState({
                contentStyle,
                placement,
            });
        }
    };

    addOutsideClickListener = () => {
        if (!__SERVER__) {
            document.addEventListener('click', this.toggleDisplayContent, false);
        }
    };

    removeOutsideClickListener = () => {
        if (!__SERVER__) {
            document.removeEventListener('click', this.toggleDisplayContent, false);
        }
    };

    toggleDisplayContent = (visibility) => {
        const { onClick, onMouseOver } = this.props;
        const { isVisible } = this.state;
        const tooltipCallback = onClick || onMouseOver;
        let toggle = true;

        if (this.containerClicked) {
            toggle = false;
            this.containerClicked = false;
        }

        if (typeof visibility !== 'boolean') {
            // eslint-disable-next-line no-param-reassign
            visibility = false;
        }

        if (toggle && visibility !== isVisible && this.tooltipWrapperRef) {
            this.setState(
                {
                    isVisible: visibility,
                    contentStyle: {},
                },
                () => {
                    if (typeof tooltipCallback === 'function') {
                        tooltipCallback(visibility);
                    }

                    this.setTooltipPosition();
                }
            );
        }
    };

    overlayClicked = () => {
        this.containerClicked = false;
        this.toggleDisplayContent(false);
    };

    render() {
        const {
            automationId,
            customTooltipTheme: { tooltipContentClass, tooltipWrapperClass },
            disableHideOnContentClick,
            enableOverlay,
            onClick,
            tooltipData,
        } = this.props;

        const { contentStyle, isVisible, placement } = this.state;

        const tooltipWrapperClasses = [
            'inline-block',
            'relative',
            cx('tooltip-wrapper', placement, tooltipWrapperClass, {
                'display-arrow': isVisible,
                'enable-overlay': enableOverlay,
            }),
        ].join(' ');

        const tooltipContentClasses = [
            'absolute',
            'bg-white-100',
            'rounded-small',
            'shadow-medium',
            'text-left',
            cx('tooltip-content', placement, tooltipContentClass, {
                'enable-overlay': enableOverlay,
            }),
        ].join(' ');

        const overlayDesignClasses = [
            'fixed',
            'h-full',
            'w-full',
            'z-40',
            cx('overlay-design', {
                'enable-overlay': enableOverlay,
                visible: enableOverlay,
                invisible: !enableOverlay,
            }),
        ].join(' ');

        const containerProps = {
            className: tooltipContentClasses,
            onClick: disableHideOnContentClick && onClick ? this.setActionOnContainer : null,
            ref: (tooltipContentRef) => {
                this.tooltipContentRef = tooltipContentRef;
            },
            style: contentStyle,
        };

        if (disableHideOnContentClick && onClick) {
            containerProps.onClick = this.setActionOnContainer;
        }

        return (
            <div
                className={tooltipWrapperClasses}
                data-automation-id={automationId}
                ref={(tooltipWrapperRef) => {
                    this.tooltipWrapperRef = tooltipWrapperRef;
                }}
            >
                {this.getTooltipText()}
                {isVisible && (
                    <div {...containerProps}>
                        {typeof tooltipData === 'function' ? tooltipData() : tooltipData}
                    </div>
                )}
                {isVisible && enableOverlay && (
                    <button
                        aria-label="dismiss overlay"
                        className={overlayDesignClasses}
                        onClick={this.overlayClicked}
                        type="button"
                    />
                )}
            </div>
        );
    }
}

export default Tooltip;
